<template>
    <div class="absolute  top-0 left-0 right-0 h-full lg:fixed lg:inset-0 lg:h-auto z-50 bg-slate-500 bg-opacity-90 flex items-center justify-center">
        <div
            class="
                parent-slides
                relative
                z-60
                w-[90%]
                max-w-4xl
                border-t-4 
                border-solid 
                border-t-orange
                rounded-md
                overflow-auto
                py-6
                px-6
                lg:py-12
                lg:px-24
                bg-white
            "
            ref="slider"
        >
            <div
                class="absolute inset-6 lg:inset-12"
                :class="{ 'z-50': step.active, 'z-10': !step.active }"
                v-for="(step, id) in steps"
                :key="'step-' + id"
            >
                <transition name="slide" @enter="enterSlide">
                    <div ref="slide" v-if="step.active">
					<!-- Logo -->
	                    <img
	                        src="/images/logo.svg"
	                        class="mx-auto mb-5 lg:mb-10"
	                        style="width: 110px; height: auto"
	                        alt="My10X"
	                    />           
                    
                        <header class="text-center mb-5">
							<img
								v-if="step.icon"
								v-bind:src="'/images/' + step.icon"							
								v-bind:alt="step.iconalt"
								class="mx-auto mb-5 w-12 h-12 lg:w-20 lg:h-20"
							/> 
                            <h3 class="text-base md:text-lg lg:text-2xl font-bold mb-3">{{ step.header }}</h3>
                        </header>
                        <div class="prose text-sm md:text-base lg:text-lg lg:prose-lg text-center mx-auto mb-5" v-html="step.content"></div>
                        <footer class="flex items-center justify-center pt-5 mb-5">
                            <button
                                class="
                                    w-auto
                                    rounded-md
                                    btn 
                                    btn-primary 
                                    !w-64
                                    m-0
                                    hover:bg-slate-500
                                    transition-colors
                                    lg:btn-lg
                                "
                                @click="next()"
                            >
                                {{ step.button_label }} <i class="fa-regular fa-lg fa-arrow-right text-orange ml-2"></i>
                            </button>
                        </footer>
                    </div>
                </transition>
            </div>
            <div class="absolute left-0 right-0 bottom-5 w-full z-60">
                <ul class="relative z-20 flex items-center justify-center w-full">
                    <li v-for="(dot, index) in steps" :key="'dot-' + index">
                        <a
                            class="
                                block
                                w-2
                                h-2
                                rounded-full
                                bg-slate-500
                                m-1
                                hover:bg-slate-300
                                transition-colors
                            "
                            :class="{ 'bg-slate-500': !dot.active, 'bg-orange-500': dot.active }"
                            @click.prevent="gotoStep(index)"
                            href="#"
                            role="button"
                        >
                            <span class="sr-only">{{ index + 1 }}</span>
                        </a>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
<script>
import { mapActions, mapState } from 'pinia';
import { useAppStore } from '@/store/app';

export default {
    props: {},
    data: () => ({
        steps: [
            {
                header: 'Welcome to My10x!',
                icon: 'welcome.svg',
                iconalt: 'Welcome to My10x!',                
                content:
                    '<p>My10x helps you get the most out of your points & miles.</p><p>Let’s quickly learn how to get the most out of My10x.</p>',
                button_label: 'Get Started',
                active: true,
            },
            {
                header: 'Load your cards into your My10x wallet to make managing them a breeze',
                icon: 'wallet.svg',
                iconalt: 'My10x Wallet',
                content:
                    '<p>Add your current credit cards to your My10x wallet to see everything in one place. This will allow my10x to analyze your current points & miles totals, track your eligibility for new card offers, calculate your 5/24 status, and more.</p><p>The best way to do this is via our bank level security connection feature, however, you can add the card details manually if you like.</p>',
                button_label: 'Continue',
                active: false,
            },
            
            {
                header: 'Track all of your loyalty accounts in one place',
                icon: 'mypoints.svg',
                iconalt: 'My Points',                
                content:
                    '<p>You can use the My Points tool to see all of your points & miles balances in one place. This information is synced automatically for many major loyalty programs if you use our bank connection tool. </p><p>Otherwise you can add and update the information manually. Don’t worry, we will remind you to do so every now and then.</p>',
                button_label: 'Continue',
                active: false,
            },
            {
                header: 'Stay on top of your credit with simple, customized advice to maximize your credit score',
                icon: 'credit-score.svg',
                iconalt: 'Credit Score',                
                content:
                    '<p>Input your credit score so that we can provide helpful tips to boost and maintain your score as you continue to earn points &amp; miles.</p>',
                button_label: 'Continue',
                active: false,
            },
            {
                header: 'Expert advice to help you choose the best card for you',
                icon: 'mynextcard.svg',
                iconalt: 'My Next Card',                
                content:
                    '<p>Our proprietary recommendation tool looks at your existing points & miles balances, credit cards, credit score, and product eligibility rules to recommend the single best credit card for you and you alone.</p><p>Allowing you to get expert advice with the click of a button.</p>',
                button_label: 'Continue',
                active: false,
            },
            {
                header: 'Invite others to join My10x and even collaborate with your points & miles strategy',
                icon: 'users.svg',
                iconalt: 'Invite Others',                
                content:
                    '<p>Once they sign up, you can request to link your My10x accounts to collaborate in “2 player mode”, which allows you to easily manage your points & miles strategy with one or more partners.</p>',
                button_label: 'Continue',
                active: false,
            },
            {
                header: 'You’re good to go!',
                icon: 'complete.svg',
                iconalt: 'Start Using My10x',                
                content:
                    '<p>That’s it. You’re ready to get started using My10x. Start filling out your account and using all of the tools and features that My10x includes.</p>',
                button_label: 'Open My10x',
                active: false,
            },
        ],
        currentStep: 0,
    }),
    computed: {
        ...mapState(useAppStore, ['onboarded']),
        activeStep() {
            return this.steps.find((item) => {
                return item.active === true;
            });
        },
        currentStepView() {
            return this.currentStep + 1;
        },
        isLastStep() {
            return this.steps.length === this.currentStep + 1;
        },
    },
    methods: {
        ...mapActions(useAppStore, ['setOnboarded']),
        next() {
            if (this.isLastStep) {
                this.setOnboarded(true);
                axios.post('/api/user-onboarded', { onboarded: true });
            }

            if (!this.isLastStep) {
                let currentActiveStep = this.steps.find((step) => {
                    return step.active === true;
                });
                currentActiveStep.active = false;
                this.currentStep = this.currentStep + 1;
                this.steps[this.currentStep].active = true;
            }
        },
        prev() {
            this.currentStep = this.currentStep - 1;
        },
        gotoStep(step) {
            let currentActiveStep = this.steps.find((step) => {
                return step.active === true;
            });
            currentActiveStep.active = false;
            this.currentStep = step;
            this.steps[this.currentStep].active = true;
        },
        enterSlide(el) {
            const height = getComputedStyle(el).height;
            console.log(this.$refs.slide);
            this.$refs.slider.style.minHeight = parseInt(height) + 175 + 'px';
        },
    },
    mounted() {
        const height = this.$refs.slide[0].offsetHeight;
        console.log(this.$refs.slide[0]);
        this.$refs.slider.style.minHeight = parseInt(height) + 175 + 'px';
    },
};
</script>
<style lang="scss">
.parent-slides {
    transition: min-height 0.7s ease-in-out;
}
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.7s ease, opacity 0.4s ease;
    transform: translate3d(0, 0, 0);
}

.slide-leave-to {
    transform: translate3d(-100%, 0, 0);
    opacity: 0;
}
.slide-enter-from {
    transform: translate3d(100%, 0, 0);
}
</style>
